import { useState, useEffect, useCallback } from "react";
import { Star, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { trpc } from "@/lib/trpc";

interface GoogleReviewsProps {
  compact?: boolean;
  variant?: "light" | "dark" | "navy";
  maxReviews?: number;
}

function StarRating({ rating, size = 16 }: { rating: number; size?: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={size}
          className={
            star <= rating
              ? "fill-yellow-400 text-yellow-400"
              : "fill-gray-200 text-gray-200"
          }
        />
      ))}
    </div>
  );
}

function ReviewCard({
  review,
  compact,
}: {
  review: {
    authorName: string;
    rating: number;
    relativeTime: string;
    text: string;
    profilePhotoUrl: string;
    authorUrl: string;
  };
  compact?: boolean;
}) {
  const maxLength = compact ? 200 : 320;
  const truncated =
    review.text.length > maxLength
      ? review.text.slice(0, maxLength).trimEnd() + "…"
      : review.text;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col h-full min-h-[220px]">
      <div className="flex items-start gap-3 mb-3">
        {review.profilePhotoUrl ? (
          <img
            src={review.profilePhotoUrl}
            alt={review.authorName}
            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
            loading="lazy"
            width="40" height="40"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-700 font-bold text-sm">
            {review.authorName.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <a
            href={review.authorUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-gray-900 text-sm hover:text-blue-600 transition-colors truncate block"
          >
            {review.authorName}
          </a>
          <div className="flex items-center gap-2 mt-0.5">
            <StarRating rating={review.rating} size={13} />
            <span className="text-xs text-gray-400">{review.relativeTime}</span>
          </div>
        </div>
      </div>
      <p className="text-gray-600 text-sm leading-relaxed flex-1">{truncated}</p>
    </div>
  );
}

export default function GoogleReviews({
  compact = false,
  variant = "light",
  maxReviews = 5,
}: GoogleReviewsProps) {
  const { data, isLoading, isError } = trpc.reviews.getGoogleReviews.useQuery(
    undefined,
    { staleTime: 1000 * 60 * 30 }
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const bgClass =
    variant === "navy"
      ? "bg-[#1e3a8a]"
      : variant === "dark"
      ? "bg-gray-900"
      : "bg-gray-50";
  const headingClass =
    variant === "light" ? "text-gray-900" : "text-white";
  const subClass =
    variant === "light" ? "text-gray-600" : "text-gray-300";

  const reviews = data ? data.reviews.slice(0, maxReviews) : [];
  const totalSlides = reviews.length;

  const next = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  // Auto-advance every 5 seconds
  useEffect(() => {
    if (isPaused || totalSlides <= 1) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [isPaused, next, totalSlides]);

  if (isLoading) {
    return (
      <section className={`py-16 px-4 ${bgClass}`}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="h-8 bg-gray-200 rounded w-64 mx-auto mb-3 animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-48 mx-auto animate-pulse" />
          </div>
          <div className="bg-white rounded-2xl p-6 animate-pulse h-48" />
        </div>
      </section>
    );
  }

  if (isError || !data || reviews.length === 0) return null;

  const fiveStarCount = data.reviews.filter((r) => r.rating === 5).length;

  return (
    <section
      id="testimonials"
      className={`py-16 px-4 ${bgClass}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-3">
            <svg viewBox="0 0 24 24" width="26" height="26" aria-label="Google">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <h2 className={`text-3xl font-bold ${headingClass}`}>
              What Our Customers Say
            </h2>
          </div>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className={`text-4xl font-bold ${headingClass}`}>
                {data.overallRating.toFixed(1)}
              </span>
              <div className="flex flex-col items-start">
                <StarRating rating={Math.round(data.overallRating)} size={18} />
                <span className={`text-sm mt-0.5 ${subClass}`}>
                  {data.totalReviews} Google Reviews
                </span>
              </div>
            </div>
            <div className={`text-sm ${subClass} hidden sm:block`}>·</div>
            <div className={`text-sm ${subClass}`}>
              {fiveStarCount} of {reviews.length} shown are 5&#8209;star
            </div>
          </div>
        </div>

        {/* Carousel */}
        <div className="relative">
          {/* Prev button */}
          <button
            onClick={prev}
            aria-label="Previous review"
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft size={20} className="text-gray-600" />
          </button>

          {/* Slide */}
          <div className="overflow-hidden mx-6">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {reviews.map((review, i) => (
                <div key={i} className="w-full flex-shrink-0 px-2">
                  <ReviewCard review={review} compact={compact} />
                </div>
              ))}
            </div>
          </div>

          {/* Next button */}
          <button
            onClick={next}
            aria-label="Next review"
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
          >
            <ChevronRight size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-6">
          {reviews.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              aria-label={`Go to review ${i + 1}`}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                i === currentIndex
                  ? "bg-[#4caf30] w-6"
                  : variant === "light"
                  ? "bg-gray-300 hover:bg-gray-400"
                  : "bg-white/40 hover:bg-white/60"
              }`}
            />
          ))}
        </div>

        {/* CTA to Google */}
        <div className="text-center mt-8">
          <a
            href={data.googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white border border-gray-200 text-gray-700 font-semibold px-6 py-3 rounded-full shadow-sm hover:shadow-md hover:border-gray-300 transition-all duration-200 text-sm"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-label="Google">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Read All {data.totalReviews} Reviews on Google
            <ExternalLink size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}
